import React, { useEffect, useState } from "react";
import { CheckCircle2, XCircle, CircleDashed, ExternalLink, Plug, Unplug } from "lucide-react";
import { adminApi } from "../../../lib/api";

const when = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
};

// Three states, not two: a key in place is not the same as data having come back.
const State = ({ row }) => {
  if (row.verified) return <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600"><CheckCircle2 size={14} /> Working</span>;
  if (row.connected) return <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-600"><CircleDashed size={14} /> Configured</span>;
  return <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-400"><XCircle size={14} /> Not connected</span>;
};

const Link = ({ href, children, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    data-testid={testid}
    className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy hover:text-orange duration-200 [transition-property:color] whitespace-nowrap"
  >
    {children} <ExternalLink size={12} />
  </a>
);

// The two integrations the admin can connect itself, through their own OAuth flow.
const Connect = ({ row, onDisconnect }) => {
  const slug = row.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  if (row.action === "gsc") {
    return row.connected ? (
      <button
        type="button"
        onClick={() => onDisconnect("gsc")}
        data-testid={`disconnect-${slug}`}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-red-600 duration-200 [transition-property:color]"
      >
        <Unplug size={12} /> Disconnect
      </button>
    ) : (
      <a
        href={adminApi.gsc.startUrl()}
        data-testid={`connect-${slug}`}
        className="inline-flex items-center gap-1.5 rounded-full bg-navy px-3 py-1.5 text-[11px] font-bold text-white hover:bg-navy/90 duration-200 [transition-property:background-color]"
      >
        <Plug size={12} /> Connect
      </a>
    );
  }
  if (row.action === "gbp") {
    return row.connected ? (
      <button
        type="button"
        onClick={() => onDisconnect("gbp")}
        data-testid={`disconnect-${slug}`}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-red-600 duration-200 [transition-property:color]"
      >
        <Unplug size={12} /> Disconnect
      </button>
    ) : (
      <a
        href={adminApi.gbp.startUrl()}
        data-testid={`connect-${slug}`}
        className="inline-flex items-center gap-1.5 rounded-full bg-navy px-3 py-1.5 text-[11px] font-bold text-white hover:bg-navy/90 duration-200 [transition-property:background-color]"
      >
        <Plug size={12} /> Connect
      </a>
    );
  }
  return null;
};

export default function IntegrationsTab() {
  const [data, setData] = useState(null);

  const load = () => adminApi.reporting.health().then(setData).catch(() => setData({ integrations: [] }));

  useEffect(() => {
    load();
  }, []);

  const disconnect = async (which) => {
    if (!window.confirm("Disconnect this integration? Reporting that depends on it will stop until it is reconnected.")) return;
    try {
      if (which === "gsc") await adminApi.gsc.disconnect();
      if (which === "gbp") await adminApi.gbp.disconnect();
    } finally {
      load();
    }
  };

  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="google-integrations">
      <p className="text-xs text-slate-500 leading-relaxed max-w-3xl">
        <strong className="text-navy">Working</strong> means data has actually come back.{" "}
        <strong className="text-navy">Configured</strong> means the credentials are in place but nothing has been seen
        yet. Keys and secrets are held server-side and are never shown here. Where an integration needs something we
        can't do from this screen, what it needs is listed against it.
      </p>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[780px]">
          <thead>
            <tr className="text-left text-slate-400 border-b border-slate-100">
              <th className="py-3 px-4">Integration</th>
              <th className="py-3 px-4">State</th>
              <th className="py-3 px-4">Account</th>
              <th className="py-3 px-4">Last seen</th>
              <th className="py-3 px-4">Connection</th>
            </tr>
          </thead>
          <tbody>
            {data.integrations.map((row) => {
              const slug = row.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <tr key={row.name} className="border-b border-slate-50 align-top" data-testid={`integration-${slug}`}>
                  <td className="py-3 px-4">
                    <p className="font-semibold text-navy">{row.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5 max-w-md leading-relaxed">{row.detail}</p>
                    {(row.needs || []).length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {row.needs.map((n) => (
                          <li key={n} className="text-[11px] text-slate-400 flex gap-1.5 max-w-md leading-relaxed">
                            <span className="text-orange font-bold">·</span>
                            <span>{n}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="py-3 px-4"><State row={row} /></td>
                  <td className="py-3 px-4 text-xs text-slate-500 [overflow-wrap:anywhere]">{row.account || "—"}</td>
                  <td className="py-3 px-4 text-xs text-slate-500 whitespace-nowrap">{when(row.last_sync)}</td>
                  <td className="py-3 px-4">
                    <div className="flex flex-col items-start gap-2">
                      <Connect row={row} onDisconnect={disconnect} />
                      {row.console_url && (
                        <Link href={row.console_url} testid={`console-${slug}`}>{row.console_label || "Open"}</Link>
                      )}
                      {row.setup_url && (
                        <Link href={row.setup_url} testid={`setup-${slug}`}>{row.setup_label || "Set up"}</Link>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">Checked {when(data.fetched_at)}</p>
    </div>
  );
}
